<template>
    <input
        type="file"
        id="files-input-v3"
        class="hidden"
        name="images"
        multiple
        accept="image/*, video/*, .cr2, .raf, .cr3, .arw, .nef, .nrw, .dng"
        @change="selectFiles"
        ref="photos-uploader-input"
    />

    <PhotoProcessingManager
        :photo-ids="uploadedPhotoIds"
        :enabled="uploadedPhotoIds.length > 0"
        @processing-complete="onProcessingComplete"
    />

    <UploadLimitModalAlertComponent
        :open="showUploadLimitModal"
        @close="showUploadLimitModal = false"
        :type="limitUploadModalType"
    />

    <DuplicateFilesModalComponent
        :open="showDuplicatesModal"
        :filesNames="duplicateFileNames"
        @close="() => {}"
        @skip="proceedWithPendingUpload(true)"
        @upload-both="proceedWithPendingUpload(false)"
        :title="String($t('photo_uploader.duplicates_title')) || 'Знайдено файли з однаковою назвою'"
        :message="String($t('photo_uploader.duplicates_message')) || 'Деякі обрані файли вже існують у галереї. Оберіть дію:'"
        :skipText="String($t('photo_uploader.skip')) || 'Пропустити'"
        :uploadBothText="String($t('photo_uploader.upload_both')) || 'Завантажити обидва'"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import {ResponseError} from "@/types";
import {isSafari} from "@/_helpers/functions";
import {getMemoryInfo, isMemoryLimitExceeded, logMemoryUsage, formatMemorySize} from "@/_helpers/memory";
import {reportMemoryError, reportUnknownError, uploading, uploadingError} from "@/data/requests/analytics";
import {registerRetryHandler, unregisterRetryHandler} from "@/_helpers/uploadRetryRegistry";

import UploadLimitModalAlertComponent from "@/components/Galleries/Modals/UploadLimitModalAlertComponent.vue";
import DuplicateFilesModalComponent from "@/components/Galleries/Modals/DuplicateFilesModalComponent.vue";
import PhotoProcessingManager from "@/components/PhotoProcessingManager.vue";


interface FileMetadata {
    id: string;
    name: string;
    size: number;
    type: string;
    lastModified: number;
}

export default defineComponent({
    name: "PhotoUploaderV3Component",

    components: {
        UploadLimitModalAlertComponent,
        DuplicateFilesModalComponent,
        PhotoProcessingManager,
    },

    props: {
        folder: Object,
    },

    data() {
        return {
            errors: [] as {fileId: string, message: string}[],
            fileMetadata: [] as FileMetadata[],

            isUploadingFiles: false,

            uploadedFileIds: [] as string[],
            uploadingFileIds: new Set<string>(),
            showUploadLimitModal: false,
            limitUploadModalType: "storage_limit",
            isDragging: false,
            showDuplicatesModal: false,
            duplicateFileNames: [] as string[],
            pendingInput: null as HTMLInputElement | null,

            uploadedPhotoIds: [] as number[],

            fileCache: new Map<string, File>(),

            memoryCheckInterval: null as number | null,
            memoryThreshold: 85,
            memoryWarningShown: false,
            currentMemoryInfo: null as any,

            currentSessionId: null as string | null,
        }
    },

    computed: {
        ...mapGetters({
            gallery: "galleries/gallery",
            photos: "photos/photos",
            user: "user/user"
        }),

        totalFiles(): number {
            return this.fileMetadata.length;
        },

        totalSize(): number {
            return this.fileMetadata.reduce((sum, meta) => sum + meta.size, 0);
        },

        uploadedSize(): number {
            return this.fileMetadata
                .filter(meta => this.uploadedFileIds.includes(meta.id))
                .reduce((sum, meta) => sum + meta.size, 0);
        },

        uploadingProgress(): number {
            const total = this.totalFiles as number;
            if (!total) return 0;
            return Math.round((this.uploadedFileIds.length / total) * 100);
        },

        isSafari(): Boolean {
            return isSafari()
        },

        uploadedSizeString(): string {
            return this.bytesToSize(this.uploadedSize as number);
        },

        totalSizeString(): string {
            return this.bytesToSize(this.totalSize as number);
        },

        memoryInfo(): string {
            if (this.currentMemoryInfo) {
                return `${formatMemorySize(this.currentMemoryInfo.usedJSHeapSize)} / ${formatMemorySize(this.currentMemoryInfo.jsHeapSizeLimit)} (${this.currentMemoryInfo.memoryUsage.toFixed(1)}%)`;
            }
            return 'N/A';
        },

        isMemoryWarning(): boolean {
            if (this.currentMemoryInfo) {
                return this.currentMemoryInfo.memoryUsage > this.memoryThreshold;
            }
            return false;
        },

        shouldShowMemoryInfo(): boolean {
            return this.user?.memory_info === true;
        }
    },

    mounted() {
        document.body.addEventListener('dragover', this.handleDragOver);
        document.body.addEventListener('dragleave', this.handleDragLeave);
        document.body.addEventListener('drop', this.handleDrop);
        // beforeunload обробляється глобально в App.vue через upload store

        if (this.shouldShowMemoryInfo) {
            this.updateMemoryInfo();

            this.memoryCheckInterval = window.setInterval(() => {
                this.updateMemoryInfo();
                this.checkMemoryLimit();
                logMemoryUsage('Memory check');
            }, 5000);
        }
    },

    beforeUnmount() {
        document.body.removeEventListener('dragover', this.handleDragOver);
        document.body.removeEventListener('dragleave', this.handleDragLeave);
        document.body.removeEventListener('drop', this.handleDrop);

        if (this.memoryCheckInterval) {
            clearInterval(this.memoryCheckInterval);
            this.memoryCheckInterval = null;
        }
        // Реєстрація retry handler прибирається коли компонент демонтується
        if (this.currentSessionId) {
            unregisterRetryHandler(this.currentSessionId);
        }
        // Завантаження продовжується у фоні — прогрес відображається у GlobalUploadProgressComponent
    },

    methods: {
        ...mapActions({
            uploadPhoto: "photos/uploadPhotoByUrl",
            startUploadSession: "upload/startUploadSession",
            finishUploadSession: "upload/finishUploadSession",
        }),

        async selectFiles(event: Event) {
            const input = event.target as HTMLInputElement;
            const filesArray = Array.from(input.files as FileList);

            this.fileMetadata = this.createFileMetadata(filesArray);

            this.uploadedFileIds = [];
            this.uploadingFileIds.clear();
            this.errors = [];
            this.uploadedPhotoIds = [];
            this.fileCache.clear();

            if (this.shouldShowMemoryInfo) {
                this.updateMemoryInfo();
            }

            input.disabled = true;

            const duplicateNames = this.findDuplicateNames(filesArray);
            if (duplicateNames.length > 0) {
                this.duplicateFileNames = duplicateNames;
                this.showDuplicatesModal = true;
                this.pendingInput = input;
                return;
            }

            await this.performUpload(input, filesArray);
        },

        async proceedWithPendingUpload(skipDuplicates: boolean) {
            if (!this.pendingInput) return;

            const input = this.pendingInput;
            const duplicateSet = new Set(this.duplicateFileNames);

            if (skipDuplicates) {
                this.fileMetadata = this.fileMetadata.filter((meta) => !duplicateSet.has(meta.name));
            }

            this.showDuplicatesModal = false;
            this.duplicateFileNames = [];

            const filesArray = Array.from(input.files as FileList).filter(file =>
                skipDuplicates ? !duplicateSet.has(file.name) : true
            );

            await this.performUpload(input, filesArray);
        },

        findDuplicateNames(selectedFiles: File[]): string[] {
            const existingNames = new Set((this.photos as any[]).map((p: any) => p.original_filename));
            const duplicates: string[] = [];
            for (const file of selectedFiles) {
                if (existingNames.has(file.name)) {
                    duplicates.push(file.name);
                }
            }
            return duplicates;
        },

        async performUpload(input: HTMLInputElement, filesArray: File[]) {
            input.value = "";

            if (this.shouldShowMemoryInfo) {
                this.updateMemoryInfo();
                this.checkMemoryLimit();
            }

            // Видаляємо старі сесії цієї галереї перед новим завантаженням
            (this.$store.getters['upload/activeSessions'] as any[])
                .filter((s: any) => s.galleryId === this.gallery?.id)
                .forEach((s: any) => {
                    this.$store.commit('upload/dismissSession', { id: s.id });
                    this.$store.commit('upload/removeSession', { id: s.id });
                });

            // Реєструємо сесію в глобальному store
            const sessionId = `${this.gallery?.id}_${Date.now()}`;
            this.currentSessionId = sessionId;

            // Реєструємо retry handler для цієї сесії (використовується GlobalUploadProgressComponent)
            registerRetryHandler(sessionId, (fileId: string) => {
                this.retryFileById(fileId);
            });

            await this.startUploadSession({
                id: sessionId,
                galleryId: this.gallery?.id || '',
                galleryName: this.gallery?.name || '',
                totalSize: this.totalSize,
                files: filesArray.map(file => ({
                    id: this.findFileIdByFile(file),
                    name: file.name,
                    size: file.size,
                    status: 'queued' as const,
                })),
            });

            let chunks: File[][] = [];
            let chunkSize = isSafari() ? 1 : 3;

            for(let i = 0; i < filesArray.length; i += chunkSize) {
                chunks.push(filesArray.slice(i, i + chunkSize));
            }

            this.isUploadingFiles = true;

            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                const chunk = chunks[chunkIndex];

                if (this.shouldShowMemoryInfo) {
                    this.updateMemoryInfo();
                    this.checkMemoryLimit();
                }

                chunk.forEach(file => {
                    const fileId = this.findFileIdByFile(file);
                    this.uploadingFileIds.add(fileId);
                    this.$store.commit('upload/setFileStatus', {
                        sessionId,
                        fileId,
                        status: 'uploading',
                    });
                });

                const promises = chunk.map(async (file) => {
                    const fileId = this.findFileIdByFile(file);
                    const startTime = performance.now();

                    try {
                        const photo = await this.uploadFile(file);

                        uploading(
                            this.gallery?.slug || '',
                            file.size,
                            file.name,
                            this.user?.id || 0,
                            startTime,
                            performance.now(),
                            { success: true },
                            () => {},
                            () => {}
                        );

                        this.uploadingFileIds.delete(fileId);
                        this.uploadedFileIds.push(fileId);

                        if (photo && photo.id) {
                            this.uploadedPhotoIds.push(photo.id);
                        }

                        // Оновлюємо прогрес і статус файлу у глобальному store
                        this.$store.commit('upload/setFileStatus', { sessionId, fileId, status: 'done' });
                        this.$store.commit('upload/updateSessionProgress', {
                            id: sessionId,
                            uploadedCount: this.uploadedFileIds.length,
                            uploadedSize: this.uploadedSize,
                        });

                    } catch (error: any) {
                        this.uploadingFileIds.delete(fileId);

                        uploadingError(
                            this.gallery?.slug || '',
                            JSON.stringify({type: 'upload_error', message: error.message}),
                            file.size,
                            file.name,
                            this.user?.id || 0,
                            startTime,
                            performance.now(),
                            () => {},
                            () => {}
                        );

                        reportUnknownError(error, {
                            galleryId: this.gallery?.id,
                            fileName: file.name,
                            fileSize: file.size,
                            memoryMonitoringEnabled: this.shouldShowMemoryInfo
                        });

                        this.fileCache.set(fileId, file);
                        this.errors.push({ fileId, message: error.message });

                        this.$store.commit('upload/setFileStatus', {
                            sessionId,
                            fileId,
                            status: 'error',
                            errorMessage: error.message,
                        });
                    }
                });

                await Promise.all(promises);

            }

            this.isUploadingFiles = false;
            input.disabled = false;

            // Завершуємо сесію в глобальному store
            await this.finishUploadSession({ id: sessionId });

            setTimeout(() => {
                this.fileMetadata = [];
                this.uploadedFileIds = [];
                this.uploadingFileIds.clear();
                // fileCache не чистимо тут — він потрібен для retry
            }, 3000);
        },

        onProcessingComplete(photoIds: number[]) {
            console.log('Photo processing completed for:', photoIds);
        },

        async uploadFile(file: File) {
            const fileId = this.findFileIdByFile(file);
            const sessionId = this.currentSessionId;

            return await this.uploadPhoto({
                file,
                gallery: this.gallery?.id,
                folder: this.folder?.id,
                onProgress: (loaded: number) => {
                    if (sessionId) {
                        this.$store.commit('upload/updateFileProgress', {
                            sessionId,
                            fileId,
                            uploadedSize: loaded,
                        });
                    }
                },
            }).catch(({type, data}) => {
                if(type == ResponseError.TooLarge) {
                    throw new Error(this.$t('photo_uploader.error_too_large'));
                }

                if(type == ResponseError.Unauthorized) {
                    throw new Error(this.$t('photo_uploader.error_unauthorized'));
                }

                if(type == ResponseError.Network) {
                    throw new Error(this.$t('photo_uploader.error_network'));
                }

                if(data.data?.type === "storage_limit") {
                    this.showUploadLimitModal = true;
                    this.limitUploadModalType = data.data.type;
                }

                if(data.data?.type === "video_limit") {
                    this.showUploadLimitModal = true;
                    this.limitUploadModalType = data.data.type;
                }

                const message = data?.message
                    || (typeof data === 'string' ? data : null)
                    || data?.statusText
                    || null;

                if (message) {
                    throw new Error(message);
                }

                throw new Error(this.$t('photo_uploader.error_unknown'));
            });
        },

        clickSelectFiles() {
            const input = this.$refs["photos-uploader-input"] as HTMLInputElement;
            input.click();
        },

        bytesToSize(bytes: any) {
            const num = Number(bytes);
            const sizes = [
                this.$t('storage.byte'),
                this.$t('storage.kilobyte'),
                this.$t('storage.megabyte'),
                this.$t('storage.gigabyte'),
                this.$t('storage.terabyte')
            ];

            if (num == 0) return '0 ' + this.$t('storage.byte');

            const i = (Math.floor(Math.log(num) / Math.log(1024)));

            return Math.round(num / Math.pow(1024, i)) + ' ' + sizes[i];
        },

        handleDragOver(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = true;
        },

        handleDragLeave(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = false;
        },

        handleDrop(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();
            this.isDragging = false;

            const files = Array.from(event.dataTransfer?.files || []);

            if (files.length) {
                const customEvent = {
                    target: {
                        files: files as unknown as FileList
                    }
                };

                this.selectFiles(customEvent as unknown as Event);
            }
        },

        createFileMetadata(files: File[]): FileMetadata[] {
            return files.map(file => ({
                id: this.generateFileId(file),
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            }));
        },

        generateFileId(file: File): string {
            return `${file.name}_${file.size}_${file.lastModified}_${Date.now()}_${Math.random()}`;
        },

        findFileIdByFile(file: File): string {
            return this.fileMetadata.find(meta =>
                meta.name === file.name &&
                meta.size === file.size &&
                meta.lastModified === file.lastModified
            )?.id || '';
        },

        getFileNameById(fileId: string): string {
            return this.fileMetadata.find(meta => meta.id === fileId)?.name || 'Unknown file';
        },

        async retryFileById(fileId: string) {
            this.errors = this.errors.filter((error) => error.fileId !== fileId);

            const file = this.fileCache.get(fileId);
            if (!file) {
                console.error('File not found in cache for retry:', fileId);
                return;
            }

            this.uploadingFileIds.add(fileId);
            const sessionId = this.currentSessionId;

            try {
                const photo = await this.uploadFile(file);

                this.uploadingFileIds.delete(fileId);
                this.uploadedFileIds.push(fileId);

                if (photo && photo.id) {
                    this.uploadedPhotoIds.push(photo.id);
                }

                this.fileCache.delete(fileId);

                if (sessionId) {
                    this.$store.commit('upload/setFileStatus', { sessionId, fileId, status: 'done' });
                    this.$store.commit('upload/updateSessionProgress', {
                        id: sessionId,
                        uploadedCount: this.uploadedFileIds.length,
                        uploadedSize: this.uploadedSize,
                    });
                }

            } catch (error: any) {
                this.uploadingFileIds.delete(fileId);
                this.errors.push({ fileId, message: error.message });

                if (sessionId) {
                    this.$store.commit('upload/setFileStatus', {
                        sessionId,
                        fileId,
                        status: 'error',
                        errorMessage: error.message,
                    });
                }
            }
        },

        updateMemoryInfo: function() {
            const info = getMemoryInfo();
            if (info) {
                this.currentMemoryInfo = info;
            }
        },

        checkMemoryLimit: function() {
            if (!this.shouldShowMemoryInfo) {
                return;
            }

            if (isMemoryLimitExceeded(this.memoryThreshold)) {
                if (!this.memoryWarningShown) {
                    this.memoryWarningShown = true;

                    reportMemoryError(
                        `Memory usage exceeded ${this.memoryThreshold}% during upload`,
                        {
                            galleryId: this.gallery?.id,
                        },
                        () => {},
                        (type, data) => {
                            console.error('Failed to report memory error:', type, data);
                        }
                    );

                    if (this.fileCache.size > 0) {
                        this.fileCache.clear();
                    }
                }
            } else {
                this.memoryWarningShown = false;
            }
        }
    },
});
</script>

<style lang="scss" scoped>
.photos-container {
    .grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }
}
</style>
